import React from 'react';

/**
 * Live-collab connection indicator for the patient header.
 *
 * This badge is ALWAYS visible while collab is active — green when the
 * WebSocket is connected, red while it's reconnecting. It used to render
 * `null` whenever things were healthy, which meant there was no way to
 * tell "connected" from "the indicator is broken", and its visibility was
 * gated on an elapsed time the page computed with `Date.now()` during
 * render — so once a stuck socket stopped triggering re-renders, the
 * elapsed value froze and the warning could never appear at all. Both of
 * those are why a dead socket looked exactly like a working one.
 *
 * Props:
 *   - connected:   socket is up (green). Anything else renders red.
 *   - hasConnected: this room has been connected at least once. Only then
 *                   is a red badge a *re*connect — on a fresh open it's
 *                   just the first handshake, and calling that
 *                   "Reconnecting" reads as though something broke.
 *   - typingLocked: the editor is currently blocked from accepting input,
 *                   which the label calls out explicitly so the user knows
 *                   why their keystrokes aren't landing.
 *   - downMs:      how long we've been disconnected, shown once it's long
 *                  enough that the user deserves a number rather than a
 *                  spinner that might be lying.
 */
export default function CollabStatusBadge({
  connected,
  hasConnected = false,
  typingLocked = false,
  downMs = 0,
}) {
  const verb = hasConnected ? 'Reconnecting' : 'Connecting';
  const elapsed = downMs >= 5000 ? ` (${Math.round(downMs / 1000)}s)` : '';
  const label = connected
    ? 'Connected'
    : typingLocked
      ? `${verb} — typing paused${elapsed}`
      : `${verb}…`;

  return (
    <span
      role="status"
      aria-live="polite"
      aria-label={connected ? 'Live collaboration connected' : `Live collaboration ${verb.toLowerCase()}`}
      data-testid="collab-status-badge"
      data-connected={connected ? 'true' : 'false'}
      className={[
        'inline-flex items-center gap-1.5 text-[11px] font-semibold',
        'px-2.5 py-1 rounded-md whitespace-nowrap shrink-0 border',
        connected
          ? 'bg-green-50 border-green-300 text-green-800 dark:bg-green-500/15 dark:border-green-500/40 dark:text-green-200'
          : 'bg-red-50 border-red-300 text-red-800 dark:bg-red-500/15 dark:border-red-500/40 dark:text-red-200',
      ].join(' ')}
    >
      <span
        aria-hidden
        className={[
          'w-2 h-2 rounded-full shrink-0',
          connected ? 'bg-green-500' : 'bg-red-500 animate-pulse',
        ].join(' ')}
      />
      {label}
    </span>
  );
}
